import {
  BarChart2,
  Building2,
  Camera,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileText,
  Flag,
  FolderOpen,
  HelpCircle,
  LayoutDashboard,
  ListChecks,
  Receipt,
  Search,
  Send,
  ShoppingCart,
  Table,
  Users,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface NavBadges {
  milestones?: number;
  tasks?: number;
  companies?: number;
  contacts?: number;
  pcos?: number;
  commitments?: number;
  documents?: number;
  buyLog?: number;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeKey?: keyof NavBadges;
  hideForContractor?: boolean;
  contractorLabel?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: "Project Detail",
    items: [
      { id: "overview", label: "Overview", icon: LayoutDashboard },
      {
        id: "milestones",
        label: "Milestones",
        icon: Flag,
        badgeKey: "milestones",
        hideForContractor: true,
      },
      { id: "tasks", label: "Tasks", icon: ListChecks, badgeKey: "tasks" },
      { id: "documents", label: "Documents", icon: FolderOpen, badgeKey: "documents" },
    ],
  },
  {
    label: "Team",
    items: [
      {
        id: "team-companies",
        label: "Companies",
        icon: Building2,
        badgeKey: "companies",
        contractorLabel: "Project Directory",
      },
      { id: "team-contacts", label: "Contacts", icon: Users, badgeKey: "contacts" },
    ],
  },
  {
    label: "Procurement",
    items: [
      {
        id: "buy-log",
        label: "Buy Log",
        icon: ShoppingCart,
        badgeKey: "buyLog",
        hideForContractor: true,
      },
      {
        id: "commitments",
        label: "Commitments",
        icon: FileText,
        badgeKey: "commitments",
        contractorLabel: "My Contract",
      },
      { id: "pcos", label: "PCOs", icon: FileText, badgeKey: "pcos" },
    ],
  },
  {
    label: "Finance",
    items: [
      { id: "sov", label: "SOV", icon: DollarSign, hideForContractor: true },
      { id: "gr-sov", label: "GR SOV", icon: Table, hideForContractor: true },
      {
        id: "pay-applications",
        label: "Pay Applications",
        icon: Receipt,
        contractorLabel: "My Pay Applications",
      },
      { id: "finance", label: "GC Cost", icon: BarChart2, hideForContractor: true },
    ],
  },
  {
    label: "Documentation",
    items: [
      { id: "site-photos", label: "Site Photos", icon: Camera },
      { id: "rfis", label: "RFIs", icon: HelpCircle },
      { id: "submittals", label: "Submittals", icon: Send },
    ],
  },
];

interface ProjectDetailNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  badges: NavBadges;
  isContractor?: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function ProjectDetailNav({
  activeTab,
  onTabChange,
  badges,
  isContractor = false,
  collapsed,
  onToggleCollapse,
}: ProjectDetailNavProps) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const displayLabel = (item: NavItem) =>
    isContractor && item.contractorLabel ? item.contractorLabel : item.label;
  const matches = (item: NavItem) =>
    !(item.hideForContractor && isContractor) &&
    (query === "" || displayLabel(item).toLowerCase().includes(query));

  const allItems = NAV_SECTIONS.flatMap((s) =>
    s.items.filter((item) => !(item.hideForContractor && isContractor)),
  );

  return (
    <>
      {/* ── Mobile: horizontal scrollable tab bar ── */}
      <div className="flex md:hidden w-full overflow-x-auto border-b border-gray-200 bg-[#f9fafb] sticky top-0 z-10 scrollbar-none">
        {allItems.map((item) => {
          const isActive = activeTab === item.id;
          const badge = item.badgeKey !== undefined ? badges[item.badgeKey] : undefined;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onTabChange(item.id)}
              className={cn(
                "relative flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium whitespace-nowrap shrink-0 border-b-2 -mb-px transition-colors",
                isActive
                  ? "border-[#334155] text-[#334155]"
                  : "border-transparent text-gray-400 hover:text-gray-600",
              )}
            >
              <Icon className="h-4 w-4" />
              {displayLabel(item)}
              {badge !== undefined && badge > 0 ? (
                <span className="absolute top-1 right-1.5 text-[8px] font-bold bg-slate-200 text-slate-600 rounded-full min-w-[13px] h-[13px] flex items-center justify-center px-0.5 tabular-nums">
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* ── Desktop: vertical sidebar ── */}
      <nav
        className={cn(
          "hidden md:flex flex-col shrink-0 border-r border-gray-100 overflow-y-auto py-2 h-full transition-[width] duration-200",
          collapsed ? "w-[52px]" : "w-[220px] pr-2",
        )}
      >
        {/* Collapse toggle */}
        <div className={cn("flex mb-1", collapsed ? "justify-center px-0" : "justify-end pr-1")}>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-slate-100 transition-colors"
            title={collapsed ? "Expand menu" : "Collapse menu"}
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        {!collapsed && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="w-full h-7 pl-7 pr-2 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-300 placeholder:text-gray-400"
              />
            </div>
          </div>
        )}

        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter(matches);
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.label} className="mb-1">
              {!collapsed && (
                <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 pt-4 pb-1.5">
                  {section.label}
                </div>
              )}
              {collapsed && <div className="pt-3" />}
              {visibleItems.map((item) => {
                const isActive = activeTab === item.id;
                const badge = item.badgeKey !== undefined ? badges[item.badgeKey] : undefined;
                const Icon = item.icon;

                return (
                  <button
                    key={item.id}
                    type="button"
                    title={collapsed ? displayLabel(item) : undefined}
                    onClick={() => onTabChange(item.id)}
                    className={cn(
                      "w-full flex items-center rounded-md text-sm transition-colors",
                      collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-3 py-1.5 text-left",
                      isActive
                        ? "bg-slate-100 text-slate-900 font-medium"
                        : "text-gray-600 hover:bg-slate-50 hover:text-slate-800",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{displayLabel(item)}</span>
                        {badge !== undefined && badge > 0 ? (
                          <span className="ml-auto text-[10px] font-semibold bg-gray-100 text-gray-500 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 tabular-nums">
                            {badge}
                          </span>
                        ) : null}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
    </>
  );
}
